import { compact } from "lodash";

import { CoreSlider } from "@boi/components-core";
import { type SliderTooltipNotch, type SliderBaseProps } from "..";
import { useIsTooltipNotches } from "./useIsTooltipNotches.hook";
import { useSafeSliderNotches } from "./SafeSliderTooltipNotches";

type Params = Pick<SliderBaseProps, "minValue" | "maxValue" | "notches">;

export const useNotchesToRelativePercent = ({
  minValue = CoreSlider.DEFAULT_MIN_VALUE,
  maxValue = CoreSlider.DEFAULT_MAX_VALUE,
  notches,
}: Params): number[] | undefined => {
  if (!notches || !notches.length) return undefined;

  const safeNotches = useSafeSliderNotches(minValue, maxValue, notches);

  const buildSafeNotchValues = () => {
    if (!useIsTooltipNotches(safeNotches)) return safeNotches as number[];

    return (safeNotches as SliderTooltipNotch[]).map(
      (notch) => notch.startValue
    );
  };

  const zeroBasedMaxValue = maxValue - minValue;
  let previouslyAllocatedPercent = 0;

  const rangeTrimmedNotchValues = buildSafeNotchValues().map(
    (notchValue: number) => {
      const zeroBasedNotchValue = notchValue - minValue;
      const absolutePercent = zeroBasedNotchValue / (zeroBasedMaxValue * 0.01);
      const relativePercent = absolutePercent - previouslyAllocatedPercent;
      previouslyAllocatedPercent += relativePercent;

      // remove notches from the start andf end of the slider
      // as this prevents the ends from being "rounded"
      return notchValue === 0 || previouslyAllocatedPercent === 100
        ? undefined
        : relativePercent;
    }
  );

  return compact(rangeTrimmedNotchValues);
};
